import Bugsnag from "@bugsnag/js";
import upperFirst from "lodash/upperFirst";
import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import CartStateProvider from "@Components/CartStateProvider";
import CartEmpty from "@Components/molecules/cart/CartEmpty";
import FooterInfos from "@Components/molecules/FooterInfos";
import CartNotificationsProvider from "@Components/NotificationsStateProvider";
import Cart from "@Components/organisms/cart/Cart";
import HeaderCart from "@Components/organisms/HeaderCart";
import WithGlobalLoader, { useGlobalLoader } from "@Components/WithGlobalLoader";
import { useScript } from "@Hooks/useScript";
import CheerzApiProvider from "@Providers/CheerzApiProvider";
import CheerzPaymentApiProvider from "@Providers/CheerzPaymentApiProvider";
import KonnektorApiProvider from "@Providers/KonnektorApiProvider";
import AddressesContent from "@Types/addresses";
import { ApiErrorResponse } from "@Types/api/error";
import CartContent, { Step } from "@Types/cart";
import { Configuration } from "@Types/cart";
import PromosContent from "@Types/promos";
import UserContent from "@Types/user";
import { convertAddressesFromApiToContent } from "@Utils/cart/addresses";
import { convertCartFromApiToContent } from "@Utils/cart/cart";
import { convertPromosFromApiToContent } from "@Utils/cart/promos";
import { getCsrfToken, getMetaContent } from "@Utils/meta";
import { setDataLayerTrack } from "@Utils/track";
import { convertUserFromApiToContent } from "@Utils/user";

interface Props {
  localizationSlug: string;
  step: Step;
}

const CartPage: FunctionComponent<Props> = ({ localizationSlug, step }) => {
  const [cart, setCart] = useState<CartContent>();
  const [addresses, setAddresses] = useState<AddressesContent>();
  const [promos, setPromos] = useState<PromosContent[]>();
  const [user, setUser] = useState<UserContent>();
  const [paymentApiProvider, setPaymentApiProvider] = useState<CheerzPaymentApiProvider>();
  const [emptyCart, setEmptyCart] = useState<boolean>(false);
  const { setLoading } = useGlobalLoader();
  const [error, loaded] = useScript("https://js.processout.com/processout.js", false, true);

  const csrfToken = getMetaContent("csrf-token");
  if (!csrfToken) {
    throw new Error("CSRF Token not found");
  }

  const cheerzApiProvider = useMemo(() => {
    return new CheerzApiProvider(localizationSlug, csrfToken);
  }, [localizationSlug, csrfToken]);
  const cheerzApiV2Provider = new CheerzApiProvider(localizationSlug, getCsrfToken(), true);
  const konnektorApiProvider = new KonnektorApiProvider(csrfToken);

  useEffect(() => {
    if (!error && loaded) {
      setPaymentApiProvider(new CheerzPaymentApiProvider());
    }
  }, [error, loaded]);

  useEffect(() => {
    setLoading(true);

    const doIt = async () => {
      const addressesResponse = await cheerzApiProvider.getAddresses();
      const cartResponse = await cheerzApiProvider.getCart();
      const promosResponse = await cheerzApiProvider.getPromos();
      const userResponse = await cheerzApiProvider.getUser();

      setAddresses(convertAddressesFromApiToContent(addressesResponse));
      setCart(convertCartFromApiToContent(cartResponse));
      setPromos(convertPromosFromApiToContent(promosResponse));
      setUser(convertUserFromApiToContent(userResponse));
      setEmptyCart(convertCartFromApiToContent(cartResponse).articles.length === 0);
    };
    void doIt();

    setLoading(false);
  }, [cheerzApiProvider, setAddresses, setLoading, setPromos, setUser]);

  useEffect(() => {
    setDataLayerTrack("Page", {
      category: "Cart",
      name: upperFirst(step),
      path: `${localizationSlug}/cart#/${step}`,
      version: "A",
    });
  }, [localizationSlug, step]);

  const configuration: Configuration = useMemo(() => {
    return {
      apiProviders: {
        cheerz: cheerzApiProvider,
        cheerzV2: cheerzApiV2Provider,
        konnektor: konnektorApiProvider,
        payment: paymentApiProvider,
      },
      localization: localizationSlug,
    };
  }, [cheerzApiProvider, konnektorApiProvider, paymentApiProvider, localizationSlug]);

  const handleValidatePostcode = useCallback(
    (postcode, countryTag) => {
      return cheerzApiProvider
        .checkPostCode(postcode, countryTag)
        .then((result: boolean) => result)
        .catch((error: ApiErrorResponse) => {
          Bugsnag.notify(
            Error(
              error.response?.data?.error_description[0]
                ? `${error.response?.data?.error_description[0]}`
                : "Error on checkPostCode call"
            )
          );
          return false;
        });
    },
    [cheerzApiProvider]
  );

  return (
    <>
      {addresses && cart && promos && user ? (
        <CartNotificationsProvider>
          <CartStateProvider
            configuration={configuration}
            initialState={{ addresses, cart, promos, selectedPaymentMethod: undefined, user }}
          >
            <HeaderCart step={step} />

            {emptyCart ? (
              <CartEmpty />
            ) : (
              <Cart
                onEmptyCart={() => setEmptyCart(true)}
                onValidatePostcode={handleValidatePostcode}
                step={step}
              />
            )}
            <FooterInfos hasPackagingInfo={true} />
          </CartStateProvider>
        </CartNotificationsProvider>
      ) : null}
    </>
  );
};

const CartPageContainer: FunctionComponent<Props> = ({ localizationSlug, step }) => {
  return (
    <WithGlobalLoader initialLoading={true}>
      <CartPage localizationSlug={localizationSlug} step={step} />
    </WithGlobalLoader>
  );
};

export default CartPageContainer;
